import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Deck-socios button. Solid brand is the default; `gradient` is the hero CTA
// (use sparingly); `ghost`/`outline`/`subtle` for secondary actions.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-semibold no-underline hover:no-underline ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:translate-y-px [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[0_1px_2px_rgba(13,161,90,.25),0_6px_16px_-6px_rgba(13,161,90,.45)] hover:bg-[#0A8449] hover:shadow-[0_2px_4px_rgba(13,161,90,.3),0_10px_22px_-8px_rgba(13,161,90,.5)]",
        gradient:
          "text-white bg-grad shadow-[0_6px_18px_-6px_rgba(13,161,90,.5)] hover:brightness-105 hover:shadow-[0_10px_26px_-8px_rgba(11,36,26,.5)]",
        outline:
          "border border-border bg-card text-foreground hover:border-primary hover:bg-primary-tint",
        subtle: "bg-secondary text-foreground hover:bg-accent",
        ghost: "text-foreground hover:bg-secondary",
        success:
          "bg-success text-success-foreground hover:brightness-95 shadow-[0_6px_16px_-6px_rgba(14,122,85,.4)]",
        destructive:
          "bg-destructive text-destructive-foreground hover:brightness-95 shadow-[0_6px_16px_-6px_rgba(200,26,34,.4)]",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-[18px] py-2",
        sm: "h-8 rounded-sm px-3 text-[13px]",
        lg: "h-12 rounded-sm px-6 text-[15px]",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        data-kit=""
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
